import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Button, StatusPill } from "@/components/kit";
import GiveForm from "@/components/giving/GiveForm";
import { OfflineGiftModal } from "@/pages/donations/Donations";
import { fmtDate } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const METHOD = { square: "Square", cash: "Cash", check: "Check" };
const STATUS_PILL = { draft: { tone: "gray", label: "Draft" }, live: { tone: "green", label: "Live" }, closed: { tone: "maroon", label: "Closed" } };

// Admin fund card page (opened by clicking a fund card, or after saving the edit
// form). Shows the full donor card (via the SAME GiveForm used to give) alongside
// staff numbers, plus this fund's gifts. Staff/Donor-preview toggle at the top.
export default function FundCardPage({ campaignId, canEdit, isSuper, onBack, onEdit, reloadKey }) {
  const [c, setC] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [tab, setTab] = useState("staff");
  const [offline, setOffline] = useState(false);

  const load = useCallback(async () => {
    const [cc, gg] = await Promise.all([
      api.get(`/campaigns/${campaignId}`),
      api.get("/donations", { params: { campaign_id: campaignId, limit: 100 } }),
    ]);
    setC(cc.data); setGifts(gg.data.items);
  }, [campaignId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  if (!c) return <div className="text-ink/60 py-8">Loading…</div>;
  const pill = STATUS_PILL[c.status] || STATUS_PILL.draft;
  const pct = c.goal_cents ? Math.min(100, Math.round((c.raised_cents / c.goal_cents) * 100)) : null;
  const previewCampaign = {
    campaign_id: c.campaign_id, title: c.title, give_label: c.is_default ? "Give to RCA" : c.title,
    story: c.story, cover_image: c.cover_image, suggested_amounts: c.suggested_amounts, is_default: c.is_default,
  };

  return (
    <div data-testid="fund-card-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <button data-testid="back-to-donations" onClick={onBack} className="text-[14px] font-semibold text-maroon inline-flex items-center gap-1">
          <Icon name="ChevronLeft" className="w-4 h-4" /> Back to Donations
        </button>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-ink/20 overflow-hidden">
            <button data-testid="view-staff" onClick={() => setTab("staff")} className={`px-3 py-2 text-[13px] font-semibold ${tab === "staff" ? "bg-ink text-cream" : "text-ink/60"}`}>Staff view</button>
            <button data-testid="view-donor" onClick={() => setTab("donor")} className={`px-3 py-2 text-[13px] font-semibold ${tab === "donor" ? "bg-ink text-cream" : "text-ink/60"}`}>Donor preview</button>
          </div>
          {canEdit && <Button data-testid="fund-edit" onClick={() => onEdit(c)}>Edit</Button>}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
        <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="donor-card">
          {c.cover_image
            ? <img src={c.cover_image} alt="" className="w-full h-48 object-cover" />
            : <div className="w-full h-32 bg-dark-olive/10 flex items-center justify-center"><Icon name="HeartHandshake" className="w-10 h-10 text-dark-olive/40" /></div>}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-zilla text-2xl font-semibold">{c.is_default ? "Give to RCA" : c.title}</h2>
              <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
            </div>
            {c.show_progress && c.goal_cents != null && (
              <div className="my-3" data-testid="donor-progress">
                <div className="h-2 rounded-full bg-ink/10 overflow-hidden"><div className="h-full bg-dark-olive rounded-full" style={{ width: `${pct ?? 0}%` }} /></div>
                <div className="text-[12px] text-ink/55 mt-1">{money(c.raised_cents)} of {money(c.goal_cents)} · {c.gift_count} gifts</div>
              </div>
            )}
            {c.story && <p className="text-[14px] text-ink/70 whitespace-pre-wrap my-3">{(c.story || "").replace(/<[^>]+>/g, "")}</p>}
            <GiveForm campaign={previewCampaign} preview onDonate={() => {}} />
          </div>
        </div>

        {tab === "staff" && (
          <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="staff-numbers">
            <div className="font-zilla text-3xl font-semibold text-dark-olive">{money2(c.raised_cents)}</div>
            <div className="text-[12px] text-ink/55 mb-3">raised{c.goal_cents ? ` · of ${money2(c.goal_cents)} goal${pct != null ? ` (${pct}%)` : ""}` : ""}</div>
            <div className="grid grid-cols-2 gap-3 text-[14px]">
              <div><div className="text-ink/50 text-[12px]">Gifts</div><div className="font-semibold">{c.gift_count}</div></div>
              <div><div className="text-ink/50 text-[12px]">Average gift</div><div className="font-semibold">{money2(c.avg_cents)}</div></div>
              <div><div className="text-ink/50 text-[12px]">Status</div><div className="font-semibold capitalize">{c.status}</div></div>
              <div><div className="text-ink/50 text-[12px]">Public link</div><div className="font-semibold truncate">/{c.slug}</div></div>
            </div>
            <div className="mt-3 pt-3 border-t border-ink/10 text-[13px]">
              Show progress publicly: <b>{c.show_progress ? "On" : "Off"}</b>
              {!c.show_progress && <div className="text-ink/50 text-[12px] mt-0.5">The public can't see these numbers. You always can.</div>}
            </div>
          </div>
        )}
      </div>

      {tab === "staff" && (
        <div className="bg-cream border border-ink/15 rounded-xl p-5 mt-5" data-testid="fund-gifts">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-zilla text-xl font-semibold">Gifts to this fund</h3>
            <div className="flex gap-2">
              <a data-testid="fund-export" href={`${API}/donations/export?campaign_id=${c.campaign_id}`} className={`border border-ink/25 rounded-lg px-4 py-2.5 min-h-[44px] text-[14px] font-semibold inline-flex items-center gap-1.5 ${canEdit ? "" : "opacity-50 pointer-events-none"}`}>
                <Icon name="Download" className="w-4 h-4" /> Export to Excel
              </a>
              {canEdit && <Button data-testid="fund-log-offline" onClick={() => setOffline(true)}><Icon name="Plus" className="w-4 h-4" /> Log an offline gift</Button>}
            </div>
          </div>
          {gifts.length === 0 ? (
            <div className="text-ink/50 text-[14px] py-6 text-center">No gifts to this fund yet.</div>
          ) : (
            <div className="divide-y divide-ink/8">{gifts.map((g) => (
              <div key={g.donation_id} className="flex items-center gap-3 py-3" data-testid={`fund-gift-${g.donation_id}`}>
                <div className="w-9 h-9 rounded-full bg-ink/10 flex items-center justify-center text-[12px] font-bold text-ink/60 shrink-0">{g.anonymous ? "–" : initials(g.donor_name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold truncate">{g.anonymous ? "Anonymous" : (g.donor_name || "—")}</div>
                  <div className="text-[12px] text-ink/55">{fmtDate(g.date)} · {METHOD[g.method] || g.method} · <span className="capitalize">{g.status}</span>{isSuper && g.simulated ? " · SIMULATED" : ""}</div>
                </div>
                <div className="font-zilla text-lg font-semibold">{money2(g.amount_cents)}</div>
              </div>
            ))}</div>
          )}
        </div>
      )}

      {offline && <OfflineGiftModal open campaigns={[{ campaign_id: c.campaign_id, title: c.title, is_default: c.is_default }]} onClose={() => setOffline(false)} onSaved={() => { setOffline(false); load(); }} />}
    </div>
  );
}
